import React, { useMemo, useRef, useState, useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { svgSprite } from '../../../hybrid/wrappers';
import { useWindowDimensions } from '../../../common/utils/windowDimensions';
import { getSortData, globalSearchFunc } from '../utils';
import UserContext from '../../../common/utils/getContext';
import GridComponent from '../../grid/GridComponent/GridComponent';
import AdminGridFilterHeader from '../components/GridFilterHeader';
import PortalFilter from '../components/PortalFilter/PortalFilter';
import T from '../../../common/components/T';
import { generateUniqueId, getFilterForPage, useWidgetTranslates } from '../../../common/utils/utils';
import css from './GridFilter.module.scss';
import { generateCompStylers } from '../../../common/utils/styles';
import classnames from 'classnames';
import { useRouter } from 'next/router';

const GridFilter = ({
    customStyles = {},
    showBy = {
        options: ['10', '25', '50', '75', '100'],
        value: 0
    },
    grid,
    filter,
    convertData,
    convertHeadData,
    textsData,
    setUrl = () => {},
    pageSettings,
    sticky = true,
    exportData,
    whenMobileSearchButton,
    component,
    token = false, // for tron
    noHeader = false
}) => {
    const [gridData, setGridData] = useState(false);
    const { userToken, setAlertData, lang } = useContext(UserContext);
    const [showModal, setShowModal] = useState(false);
    const compStyles = useMemo(() => ({ ...css, ...customStyles }), [customStyles]);
    const { stylers, classes } = generateCompStylers(compStyles);
    const { stylersGrid, classesGrid } = generateCompStylers(grid?.gridStyles, 'Grid');
    const dataFiltersToUrl = useRef(false);
    const dataFiltersItemsCountToUrl = useRef(
        showBy?.options[showBy.value] && !filter?.hidePaginationSettings
            ? `page[size]=${showBy?.options && showBy.options[showBy.value]}`
            : false
    );
    const inputGlobFilter = useState(null);
    const textToGlobalSearch = useRef('');
    const widthDimensions = useWindowDimensions();
    const screenWidth = useMemo(
        () => widthDimensions < (!!whenMobileSearchButton ? whenMobileSearchButton : 992),
        [whenMobileSearchButton, widthDimensions]
    );
    const router = useRouter();
    const interval = useRef(null);
    useEffect(() => {
        if (grid.setInitData && !grid?.localUpdate) {
            grid.setInitData(grid.initData);
        } else {
            setGridData(grid.initData);
        }
        return () => {
            clearTimeout(interval.current);
        };
    }, [grid, interval]);
    const [sort, setSort] = useState(false);

    const search = useCallback(
        async (text) => {
            const setDataFiltersToUrl = (data) => {
                if (filter?.setFiltersToParent) {
                    filter?.setFiltersToParent(data);
                }
                dataFiltersToUrl.current = data;
            };
            const setTextToGlobalSearch = (data) => (textToGlobalSearch.current = data);

            const sortParams = sort ? `sort=${sort}` : '';
            let filterParams = [sortParams, filter.filterParams].filter((f) => f).join('&');

            if (text && filter?.startFilterValueGlobeSearch) {
                filterParams += `${filter.startFilterValueGlobeSearch}`;
            }
            const setData = (grid_data, clear_data) => {
                if (grid?.setInitData) {
                    grid.setInitData(grid_data);
                } else {
                    setGridData(grid_data);
                }
                if (grid?.setClearFilterResults) {
                    grid?.setClearFilterResults(clear_data, text);
                }
            };

            await globalSearchFunc(
                text,
                setDataFiltersToUrl,
                setTextToGlobalSearch,
                convertData,
                dataFiltersItemsCountToUrl,
                setData,
                userToken,
                lang,
                filter.url || null,
                filterParams,
                setUrl,
                token,
                filter?.categoryView || false,
                filter,
                router
            );
        },
        [convertData, filter, grid, lang, sort, setUrl, token, userToken, router]
    );
    const globalSearch = useCallback(
        (text) => {
            inputGlobFilter?.[1](text);
            clearTimeout(interval.current);
            interval.current = setTimeout(async () => {
                await search(text);
            }, 500);
        },
        [inputGlobFilter, search]
    );

    useEffect(() => {
        const pageLocalFilters = getFilterForPage(router?.route);
        if (pageLocalFilters) {
            search('');
        }
    }, [router?.route]);

    const translates = useWidgetTranslates('gridFilter');

    const thead = useMemo(() => (grid.setInitData ? grid.initData.thead : gridData?.thead), [grid, gridData]);

    const sortData = useMemo(() => {
        return getSortData(
            thead?.data,
            sort,
            filter,
            dataFiltersToUrl,
            token,
            userToken,
            lang,
            grid.setInitData || setGridData,
            setUrl,
            convertData,
            setSort,
            dataFiltersItemsCountToUrl
        );
    }, [convertData, filter, grid.setInitData, lang, setUrl, sort, thead?.data, token, userToken]);

    return (
        <>
            {noHeader ? null : (
                <AdminGridFilterHeader
                    setUrl={setUrl}
                    translates={translates}
                    compStyles={compStyles}
                    screenWidth={screenWidth}
                    selectPageItemsArr={showBy?.options}
                    selectPageItemsValue={showBy?.value}
                    dataFiltersToUrl={dataFiltersToUrl}
                    dataFiltersItemsCountToUrl={dataFiltersItemsCountToUrl}
                    setGridData={grid.setInitData || setGridData}
                    convertData={convertData}
                    userToken={userToken}
                    lang={lang}
                    inputGlobFilter={inputGlobFilter}
                    textsData={textsData}
                    setShowModal={setShowModal}
                    globalSearch={globalSearch}
                    setAlertData={setAlertData}
                    pageSettings={pageSettings}
                    url={filter.url || null}
                    exportData={exportData}
                    filterParams={filter.filterParams || null}
                    token={token}
                    filter={filter}
                    sort={sort}
                />
            )}
            {screenWidth &&
            !pageSettings?.searchFilter &&
            ((grid.setInitData && grid.initData?.tbody?.data?.length) || gridData?.tbody?.data?.length) ? (
                <div className={classes.searchMobileButton} style={stylers.searchMobileButton} onClick={() => setShowModal(true)}>
                    {svgSprite('search', {
                        className: classes.searchMobileButtonSvg,
                        style: stylers.searchMobileButtonSvg
                    })}
                </div>
            ) : null}
            <PortalFilter
                setUrl={setUrl}
                pageSettings={pageSettings}
                compStyles={compStyles}
                screenWidth={screenWidth}
                textsData={textsData}
                inputGlobFilter={inputGlobFilter}
                globalSearchFunc={search}
                filter={filter}
                grid={grid}
                setShowModal={setShowModal}
                showModal={showModal}
                dataFiltersToUrl={dataFiltersToUrl}
                dataFiltersItemsCountToUrl={dataFiltersItemsCountToUrl}
                setGridData={grid.setInitData || setGridData}
                convertData={convertData}
                convertHeadData={convertHeadData}
                fullWidth='100%'
                token={token}
                translates={translates}
                sort={sort}
            />
            {(grid.setInitData && grid.initData?.tbody?.data?.length) || gridData?.tbody?.data?.length ? (
                <GridComponent
                    cellWidthArray={grid.cellWidthArray || false}
                    sticky={sticky}
                    cellThAddButton={grid?.cellThAddButton || null}
                    setUrl={setUrl}
                    setViewData={grid.setInitData || null}
                    viewThead={thead}
                    viewData={grid.setInitData ? grid.initData.tbody : gridData.tbody}
                    gridName={grid.name || 'AdminGrid'}
                    setGridData={grid.setInitData || setGridData}
                    convertData={convertData}
                    variant={grid.variant || 2}
                    handlers={grid.handlers || {}}
                    openSwitcher={grid.openSwitcher || null}
                    gridStyles={grid?.gridStyles || {}}
                    gridStylesCss={grid?.gridStylesCss || {}}
                    pageSettings={{ ...(pageSettings || {}), sort: sortData }}
                    component={component}
                    token={token}
                    filter={filter}
                />
            ) : (
                <div
                    className={classnames(classes.noDataAlertWrapper, classesGrid.gridWrapper)}
                    style={{
                        ...stylers.noDataAlertWrapper,
                        ...(stylersGrid.gridWrapper || {})
                    }}
                >
                    <div className={classes.noDataAlertWrap} style={stylers.noDataAlertWrap} key={generateUniqueId()}>
                        <div className={classes.noDataAlert} style={stylers.noDataAlert}>
                            <p>
                                {textsData?.emptyTableData || textsData?.emptyTableDataCustom || (
                                    <T
                                        textName='noDataAlert'
                                        defaultStr='По Вашему запросу не было найдено ни одного совпадения. Измените поисковый запрос или воспользуйтесь расширенным поиском.'
                                        page='all'
                                    />
                                )}
                            </p>
                        </div>
                        {screenWidth ? (
                            <div
                                className={classes.noDataAlertButton}
                                style={stylers.noDataAlertButton}
                                onClick={() => setShowModal(true)}
                            >
                                <p>
                                    <T textName='noDataAlertButton' defaultStr='Задать другие параметры' page='all' />
                                </p>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </>
    );
};
GridFilter.propTypes = {
    styles: PropTypes.object,
    customStyles: PropTypes.object,
    showBy: PropTypes.object,
    grid: PropTypes.object,
    filter: PropTypes.object,
    convertData: PropTypes.func,
    convertHeadData: PropTypes.func,
    textsData: PropTypes.object,
    setUrl: PropTypes.func,
    pageSettings: PropTypes.object,
    sticky: PropTypes.bool,
    exportData: PropTypes.object,
    whenMobileSearchButton: PropTypes.number,
    token: PropTypes.string
};
export default GridFilter;
